/**
 * The button's shape, for something that is not a button.
 *
 * A "way out" offered by an empty state is almost always navigation, and
 * navigation is an anchor: it has to be openable in a new tab, copyable as an
 * address, and followed by the browser rather than by a click handler. `Button`
 * renders a `<button>` and cannot be one. Making it polymorphic was the
 * alternative and it is worse twice over: an `as` prop defeats the
 * discriminated union that currently makes a nameless icon-only button a
 * compile error, and a `Link` variant would mean the primitives group importing
 * the router, which is the one thing that group is not allowed to do.
 *
 * So the shape leaves and the element stays where it belongs:
 *
 *     <Link className={buttonShape({ variant: 'primary' })} to="/broadcasts/new">
 *
 * Four screens had already needed this and solved it three different ways: one
 * composed the class names into its own stylesheet, which couples a screen to
 * another component's internal class names; two left the link looking like
 * prose; one styled a bare anchor by hand. That is how a console ends up with
 * two visual grammars for the same action.
 *
 * It lives in its own module rather than beside the component because a file
 * that exports both a component and a plain function gives up fast refresh for
 * the component, and `Button` is in the render path of every screen here.
 */

import type { ButtonSize, ButtonVariant } from './Button'
import styles from './Button.module.css'

export interface ButtonShape {
  variant?: ButtonVariant
  size?: ButtonSize
  fullWidth?: boolean
}

export function buttonShape({
  variant = 'secondary',
  size = 'md',
  fullWidth = false,
}: ButtonShape = {}): string {
  return [
    styles.button,
    styles[variant],
    size === 'sm' ? styles.sm : '',
    size === 'lg' ? styles.lg : '',
    fullWidth ? styles.fullWidth : '',
  ]
    .filter(Boolean)
    .join(' ')
}
